import { s } from '@/theme/tokens';
import type { DetailItem } from './resultsFileDetailUtils';
import type { Theme } from '@/ui/primitives/types';

type MetadataRow = {
  name: string;
  runLabel: string;
  statusLabel: string;
  durationLabel: string;
};

type ExecutionMetadataGridTemplateInput = {
  theme: Theme;
  rows: MetadataRow[];
  hasLatestChip: boolean;
  nameTypography: 'subtitle1' | 'body2';
  fallbackNameWidthCh: number;
  fallbackStatusWidthCh: number;
  fallbackDurationWidthCh: number;
  namePaddingPx: number;
  runPaddingPx: number;
  statusPaddingPx: number;
  durationPaddingPx: number;
};

const measureValuesWidth = (
  context: CanvasRenderingContext2D,
  values: string[],
  paddingPx: number,
): number => {
  const maxWidth = values.reduce((currentMax, value) => {
    const width = context.measureText(value).width;
    return width > currentMax ? width : currentMax;
  }, 0);
  return Math.ceil(maxWidth + paddingPx);
};

const resolveTypographyFont = (
  theme: Theme,
  variant: 'subtitle1' | 'body2',
  fallbackWeight: number,
): string => {
  const typography = theme.typography[variant];
  const fontSize = typography.fontSize ?? '14px';
  const fontFamily = typography.fontFamily ?? 'sans-serif';
  const variantWeight =
    variant === 'subtitle1'
      ? theme.typography.fontWeightRegular
      : (typography.fontWeight ?? theme.typography.fontWeightRegular);
  const fontWeight = variantWeight ?? fallbackWeight;
  return `${fontWeight} ${fontSize} ${fontFamily}`;
};

const buildFallbackTemplate = (
  fallbackNameWidthCh: number,
  hasLatestChip: boolean,
  fallbackStatusWidthCh: number,
  fallbackDurationWidthCh: number,
): string =>
  `${fallbackNameWidthCh}ch 6ch ${hasLatestChip ? 'max-content' : '0px'} ${fallbackStatusWidthCh}ch ${fallbackDurationWidthCh}ch`;

export const buildExecutionMetadataGridTemplate = ({
  theme,
  rows,
  hasLatestChip,
  nameTypography,
  fallbackNameWidthCh,
  fallbackStatusWidthCh,
  fallbackDurationWidthCh,
  namePaddingPx,
  runPaddingPx,
  statusPaddingPx,
  durationPaddingPx,
}: ExecutionMetadataGridTemplateInput): string => {
  const fallbackTemplate = buildFallbackTemplate(
    fallbackNameWidthCh,
    hasLatestChip,
    fallbackStatusWidthCh,
    fallbackDurationWidthCh,
  );
  if (rows.length === 0) {
    return fallbackTemplate;
  }
  if (typeof document === 'undefined') {
    return fallbackTemplate;
  }
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return fallbackTemplate;
  }

  context.font = resolveTypographyFont(theme, nameTypography, 400);
  const nameWidth = measureValuesWidth(
    context,
    rows.map((row) => row.name),
    namePaddingPx,
  );

  context.font = resolveTypographyFont(theme, 'body2', 400);
  const runWidth = measureValuesWidth(
    context,
    rows.map((row) => row.runLabel),
    runPaddingPx,
  );
  const statusWidth = measureValuesWidth(
    context,
    rows.map((row) => row.statusLabel),
    statusPaddingPx,
  );
  const durationWidth = measureValuesWidth(
    context,
    rows.map((row) => row.durationLabel),
    durationPaddingPx,
  );
  const latestColumn = hasLatestChip ? 'max-content' : '0px';

  return `${nameWidth}px ${runWidth}px ${latestColumn} ${statusWidth}px ${durationWidth}px`;
};

export const measureOutputPanelWidthPx = ({
  items,
  theme,
}: {
  items: DetailItem[];
  theme: Theme;
}): number => {
  if (typeof document === 'undefined') return 640;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return 640;

  const bodyFontSize = theme.typography.body2.fontSize ?? '14px';
  const bodyFontWeight = theme.typography.body2.fontWeight ?? theme.typography.fontWeightRegular;
  const monoFontFamily = theme.typography.mono.fontFamily ?? 'monospace';
  context.font = `${bodyFontWeight ?? 400} ${bodyFontSize} ${monoFontFamily}`;

  const longestLineWidth = items.reduce((currentMax, item) => {
    const lines = item.value.split('\n');
    const itemMax = lines.reduce((lineMax, line) => {
      const measured = context.measureText(line).width;
      return measured > lineMax ? measured : lineMax;
    }, 0);
    return itemMax > currentMax ? itemMax : currentMax;
  }, 0);

  const horizontalPaddingPx = 48;
  const copyButtonBufferPx = 40;
  const borderBufferPx = 2;
  return Math.ceil(longestLineWidth + horizontalPaddingPx + copyButtonBufferPx + borderBufferPx);
};

export const EXECUTION_ROW_COLUMN_GAP = s('sm');

export const LATEST_CHIP_COLUMN_SX = {
  minWidth: 0,
  marginLeft: '-4px',
  paddingRight: s('md'),
} as const;
